'use client';

/**
 * App Layout Wrapper
 *
 * Shared layout component for authenticated app sections (chat, create, generate, etc.)
 * Includes the AppNavbar + sidebar for consistent experience across all pages.
 * NO footer for app pages (unlike marketing pages).
 */
import { type CSSProperties, useState } from 'react';

import {
  AppNavbar,
  MobileBottomNav,
  Sidebar,
  SidebarProvider,
  useSidebar,
} from '~/components/navigation';
import { PROMO_BANNER_HEIGHT_PX } from '~/components/promo/promo-banner-constants';
import { ScrollGlow } from '~/components/scroll-glow';

interface AppLayoutWrapperProps {
  children: React.ReactNode;
  showCoinBalance?: boolean;
  /** Start the desktop sidebar collapsed — for pages that want the width. */
  initialCollapsed?: boolean;
}

function AppLayoutContent({
  children,
  showCoinBalance = true,
}: AppLayoutWrapperProps) {
  const { sidebarWidth, isAnimating } = useSidebar();
  // Value only — nothing sets this yet; the banner is toggled by config.
  const [promoBannerVisible] = useState(false);

  return (
    <div
      className="relative min-h-screen overflow-x-clip"
      style={
        {
          // Per-theme app gradient (styles/eros-theme.css).
          background: 'var(--eros-app-gradient)',
          // WELCOME10 marquee height — the fixed navbar, sidebar and content
          // paddings all key off this variable so the banner pushes the
          // whole chrome down while it's visible.
          '--promo-banner-h': promoBannerVisible
            ? `${PROMO_BANNER_HEIGHT_PX}px`
            : '0px',
        } as CSSProperties
      }
    >
      {/* Ambient purple glow that eases with scroll — same as the main page */}
      <ScrollGlow />

      {/* WELCOME10 promo marquee — pinned above the navbar */}

      {/* Full-width top navbar */}
      <AppNavbar showCoinBalance={showCoinBalance} />

      {/* Sidebar — fixed, below the navbar */}
      <Sidebar />

      {/* Main content — right of the sidebar, below the navbar */}
      <div
        className={`relative z-10 ml-0 flex min-h-screen max-w-full flex-col overflow-x-clip lg:ml-[var(--sb-w)] ${isAnimating ? 'transition-all duration-300 ease-in-out' : ''}`}
        style={
          {
            // Desktop-only sidebar offset; the mobile sidebar is an overlay.
            '--sb-w': `${sidebarWidth}px`,
            // Clears the fixed 64px navbar plus the promo banner (if shown)
            paddingTop: 'calc(64px + var(--promo-banner-h, 0px))',
          } as CSSProperties
        }
      >
        {/* Page Content */}
        <main className="flex flex-1 flex-col overflow-x-clip pb-16 lg:pb-0">
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}

export function AppLayoutWrapper({
  initialCollapsed = false,
  ...props
}: AppLayoutWrapperProps) {
  return (
    <SidebarProvider initialCollapsed={initialCollapsed}>
      <AppLayoutContent {...props} />
    </SidebarProvider>
  );
}
